import { alpha } from '@mui/material';
import { brand, gray, red } from '../themePrimitives';
import { Components, Theme } from '@mui/material/styles';

export const MUIButton: Components<Theme> = {
    MuiButton: {
        styleOverrides: {
            root: ({ theme }) => ({
                boxShadow: 'none',
                borderRadius: theme.shape.borderRadius,
                textTransform: 'none',
                whiteSpace: 'nowrap'
            })
        },
        variants: [
            {
                props: { size: 'small' },
                style: {
                    height: 'max-content',
                    padding: '4px 6px'
                }
            },
            {
                props: { size: 'large' },
                style: {
                    height: '3rem',
                    whiteSpace: 'nowrap'
                }
            },
            {
                props: { color: 'primary', variant: 'contained' },
                style: ({ theme }) => ({
                    'color': 'white',
                    'backgroundColor': brand[300],
                    'boxShadow': 'none',
                    'border': 'none',
                    'fontSize': '16px',
                    'whiteSpace': 'nowrap',
                    'fontWeight': '500',
                    '&:hover': {
                        backgroundImage: 'none',
                        backgroundColor: brand[400],
                        boxShadow: 'none'
                    },
                    '&:active': {
                        backgroundColor: brand[500]
                    },
                    '&.Mui-disabled': {
                        backgroundColor: brand[100],
                        color: 'white'
                    },
                    ...theme.applyStyles('dark', {
                        'color': 'white',
                        'backgroundColor': brand[300],
                        'backgroundImage': `linear-gradient(to bottom, ${brand[300]}, ${brand[300]})`,
                        'boxShadow': 'none',
                        'border': 'none',
                        '&:hover': {
                            backgroundImage: 'none',
                            backgroundColor: brand[400],
                            boxShadow: 'none'
                        },
                        '&:active': {
                            backgroundColor: brand[500]
                        }
                    })
                })
            },
            {
                props: { color: 'secondary', variant: 'contained' },
                style: {
                    'color': gray[400],
                    'backgroundColor': gray[100],
                    'whiteSpace': 'nowrap',
                    'backgroundImage': 'white',
                    '&:hover': {
                        backgroundColor: gray[700],
                        boxShadow: 'none'
                    },
                    '&:active': {
                        backgroundColor: gray[700],
                        backgroundImage: 'none'
                    },
                    '&.Mui-disabled': {
                        backgroundColor: gray[100],
                        color: gray[400]
                    }
                }
            },
            {
                props: { color: 'error', variant: 'contained' },
                style: {
                    'color': 'white',
                    'backgroundColor': red[300],
                    'whiteSpace': 'nowrap',
                    'backgroundImage': 'white',
                    'border': `1px solid ${red[300]}`,
                    '&:hover': {
                        backgroundColor: red[700],
                        boxShadow: 'none'
                    },
                    '&:active': {
                        backgroundColor: red[700],
                        backgroundImage: 'none'
                    }
                }
            },
            {
                props: { variant: 'outlined' },
                style: ({ theme }) => ({
                    'color': brand[300],
                    'border': '1px solid',
                    'borderColor': brand[200],
                    'backgroundColor': alpha(gray[50], 0.3),
                    '&:hover': {
                        backgroundColor: brand[100],
                        borderColor: brand[300]
                    },
                    '&:active': {
                        backgroundColor: brand[200]
                    },
                    ...theme.applyStyles('dark', {
                        'backgroundColor': brand[800],
                        'borderColor': brand[700],
                        '&:hover': {
                            backgroundColor: brand[900],
                            borderColor: brand[600]
                        },
                        '&:active': {
                            backgroundColor: brand[900]
                        }
                    })
                })
            },
            {
                props: { variant: 'outlined', color: 'error' },
                style: ({ theme }) => ({
                    'color': red[300],
                    'border': '1px solid ',
                    'borderColor': red[300],
                    '&:hover': {
                        backgroundColor: red[100],
                        borderColor: red[400]
                    },
                    ...theme.applyStyles('dark', {
                        'borderColor': red[300],
                        '&:hover': {
                            borderColor: red[300]
                        }
                    })
                })
            },
            {
                props: { color: 'secondary', variant: 'outlined' },
                style: ({ theme }) => ({
                    'color': gray[400],
                    'border': '1px solid',
                    'borderColor': gray[200],
                    'backgroundColor': 'white',
                    '&:hover': {
                        backgroundColor: gray[100],
                        borderColor: gray[400]
                    },
                    '&:active': {
                        backgroundColor: alpha(gray[200], 0.7)
                    },
                    ...theme.applyStyles('dark', {
                        'color': gray[50],
                        'border': '1px solid',
                        'borderColor': gray[900],
                        'backgroundColor': alpha(gray[900], 0.3),
                        '&:hover': {
                            borderColor: gray[700],
                            backgroundColor: alpha(gray[900], 0.6)
                        },
                        '&:active': {
                            backgroundColor: alpha(gray[900], 0.5)
                        }
                    })
                })
            },
            {
                props: { variant: 'text' },
                style: ({ theme }) => ({
                    'color': brand[600],
                    '&:hover': {
                        backgroundColor: brand[100]
                    },
                    '&:active': {
                        backgroundColor: brand[200]
                    },
                    ...theme.applyStyles('dark', {
                        'color': brand[50],
                        '&:hover': {
                            backgroundColor: brand[700]
                        },
                        '&:active': {
                            backgroundColor: alpha(brand[700], 0.7)
                        }
                    })
                })
            },
            {
                props: { color: 'secondary', variant: 'text' },
                style: ({ theme }) => ({
                    'color': gray[700],
                    '&:hover': {
                        backgroundColor: gray[100]
                    },
                    '&:active': {
                        backgroundColor: gray[200]
                    },
                    ...theme.applyStyles('dark', {
                        'color': brand[100],
                        '&:hover': {
                            backgroundColor: alpha(gray[900], 0.5)
                        },
                        '&:active': {
                            backgroundColor: alpha(gray[900], 0.3)
                        }
                    })
                })
            },
            {
                props: { color: 'error', variant: 'text' },
                style: ({ theme }) => ({
                    'color': red[300],
                    '&:hover': {
                        backgroundColor: alpha(red[100], 0.5)
                    },
                    '&:active': {
                        backgroundColor: alpha(red[200], 0.7)
                    },
                    ...theme.applyStyles('dark', {
                        'color': brand[100],
                        '&:hover': {
                            backgroundColor: alpha(gray[900], 0.5)
                        },
                        '&:active': {
                            backgroundColor: alpha(gray[900], 0.3)
                        }
                    })
                })
            }
        ]
    }
};
